/**
 * The passphrase wordlist.
 *
 * 4.4 says the encryption key is derived from "a caregiver passphrase". A caregiver-chosen passphrase
 * would be the weakest link in a chain whose other end is AES-256, so the app generates one instead —
 * six words from this list. That is the difference between a secret somebody invents under mild pressure
 * in a settings screen and one that is actually strong.
 *
 * **The constraints are all about transcription, not cryptography.** This passphrase gets written on a
 * card by hand, kept in a drawer for years, and typed once on a replacement device by somebody who is
 * probably upset at the time. So:
 *
 *  - **No umlauts.** `ä`, `ö` and `ü` are ambiguous on paper (`ae`?) and awkward on a system keyboard.
 *  - **No `ß`**, per the project rule — and `strasse` below is the rule working as intended.
 *  - Concrete nouns only, lowercase, 3–9 letters. Concrete words survive being read back in bad light;
 *    abstractions get misremembered as synonyms.
 *  - Nothing that reads as a slur, a diagnosis, or a joke about the person's condition. This list gets
 *    printed and handed to a family.
 *
 * **Entropy, stated honestly:** 256 words is 8 bits each, so six words is **48 bits**. That is not a
 * lot on its own — it is the 600 000 PBKDF2 iterations that make it expensive, and the two numbers have
 * to be read together (see `passphrase.ts`). Adding a seventh word is the cheap way to buy 8 more bits
 * if that trade ever needs revisiting; lengthening the list is the expensive way.
 */

export const WORDLIST: readonly string[] = [
  'abend', 'ampel', 'anker', 'apfel', 'arm', 'ast', 'atlas', 'auge',
  'balkon', 'banane', 'bank', 'baum', 'becher', 'berg', 'besen', 'bett',
  'birne', 'blatt', 'blume', 'boden', 'bogen', 'boot', 'brief', 'brot',
  'brunnen', 'buch', 'butter', 'dach', 'dame', 'dampf', 'decke', 'diesel',
  'ding', 'donner', 'dorf', 'drache', 'draht', 'dunst', 'eimer', 'eis',
  'elefant', 'ente', 'erde', 'esel', 'eule', 'fabrik', 'faden', 'fahne',
  'falke', 'farbe', 'feder', 'feld', 'fels', 'fenster', 'ferien', 'feuer',
  'fisch', 'flasche', 'fliege', 'floss', 'flur', 'fluss', 'form', 'foto',
  'frage', 'frost', 'frucht', 'fuchs', 'gabel', 'garten', 'gast', 'geist',
  'geld', 'gerste', 'gesang', 'gitter', 'glas', 'glocke', 'gold', 'gras',
  'grenze', 'gruppe', 'gurke', 'hafen', 'hafer', 'hagel', 'hahn', 'haken',
  'halle', 'hammer', 'hand', 'harfe', 'hase', 'haus', 'heft', 'heide',
  'herbst', 'herd', 'hirsch', 'hobel', 'hof', 'holz', 'honig', 'hotel',
  'hund', 'hut', 'igel', 'insel', 'jacke', 'jahr', 'kabel', 'kachel',
  'kaffee', 'kalb', 'kamel', 'kamin', 'kanne', 'karte', 'kasten', 'katze',
  'kegel', 'keller', 'kerze', 'kessel', 'kette', 'kiefer', 'kind', 'kirche',
  'kissen', 'kiste', 'klavier', 'kleid', 'klinge', 'knopf', 'koffer', 'kohle',
  'kompass', 'kopf', 'korb', 'korn', 'kran', 'kreide', 'kreis', 'kuchen',
  'kugel', 'kupfer', 'lampe', 'land', 'laterne', 'laub', 'leder', 'lehm',
  'leiter', 'licht', 'lied', 'linde', 'linie', 'lippe', 'liste', 'lohn',
  'luft', 'lupe', 'magnet', 'mandel', 'mantel', 'markt', 'mast', 'mauer',
  'maus', 'meer', 'mehl', 'messer', 'metall', 'minute', 'mittag', 'monat',
  'mond', 'moos', 'morgen', 'motor', 'mund', 'narbe', 'nase', 'nebel',
  'nelke', 'nest', 'netz', 'niete', 'norden', 'nudel', 'nummer', 'oase',
  'obst', 'ofen', 'orgel', 'ort', 'osten', 'paket', 'palme', 'papier',
  'park', 'pfad', 'pfeife', 'pferd', 'pflanze', 'pilz', 'pinsel', 'planet',
  'platte', 'platz', 'pokal', 'post', 'puppe', 'quelle', 'rad', 'rahmen',
  'rakete', 'rand', 'rasen', 'raster', 'ratte', 'raum', 'regal', 'regen',
  'reifen', 'reis', 'reise', 'rest', 'richter', 'riegel', 'rinde', 'ring',
  'rippe', 'rolle', 'rose', 'rost', 'ruder', 'sache', 'sack', 'saft',
  'sahne', 'salat', 'salz', 'samen', 'sand', 'satz', 'schaf', 'schale',
  'schere', 'schiff', 'schild', 'schirm', 'schlaf', 'schloss', 'schnee', 'schrank',
];
